import type { LandingCompanyPlatformHeroContent, LandingContent, LandingLocale } from '../content/landingContent.js';

/** Exact shipped revision-19 defaults only; never replace custom editorial values. */
const previousCopy: Record<LandingLocale, Omit<LandingCompanyPlatformHeroContent, 'matching' | 'benefits' | 'portraits'>> = {
  de: {
    badge: '', title: 'Endlich ein Matchingtool für Bewerbungen.', highlight: 'Matchingtool',
    intro: 'Viva Porta verbindet Sie mit geprüften internationalen Fachkräften und bündelt Profile, Matching und nächste Schritte in einer klaren Plattform.',
    primaryAction: { label: 'Matching live erleben', href: '#matching-demo' }, secondaryAction: { label: 'Personalbedarf besprechen', href: '#kontakt' },
    demoTitle: 'Passende Profile für Ihren Personalbedarf', demoIntro: 'Suchen, vergleichen und Interesse vormerken – so übersichtlich arbeitet Ihr Team mit Viva Porta.', demoCtaTitle: 'Bereit für Ihr erstes Match?', demoCtaLabel: 'Personalbedarf besprechen',
  },
  en: {
    badge: '', title: 'Finally, one matching tool for every application.', highlight: 'matching tool',
    intro: 'Viva Porta connects you with vetted international professionals and brings profiles, matching and every next step together in one clear platform.',
    primaryAction: { label: 'Experience matching live', href: '#matching-demo' }, secondaryAction: { label: 'Discuss staffing needs', href: '#kontakt' },
    demoTitle: 'Profiles matched to your staffing needs', demoIntro: 'Search, compare and register interest – this is how clearly your team works with Viva Porta.', demoCtaTitle: 'Ready for your first match?', demoCtaLabel: 'Discuss staffing needs',
  },
  tr: {
    badge: '', title: 'Başvurular için nihayet tek bir eşleştirme aracı.', highlight: 'eşleştirme aracı',
    intro: 'Viva Porta sizi doğrulanmış uluslararası uzmanlarla buluşturur; profilleri, eşleştirmeyi ve sonraki tüm adımları anlaşılır bir platformda birleştirir.',
    primaryAction: { label: 'Eşleştirmeyi canlı deneyin', href: '#matching-demo' }, secondaryAction: { label: 'Personel ihtiyacını görüşün', href: '#kontakt' },
    demoTitle: 'Personel ihtiyacınıza uygun profiller', demoIntro: 'Arayın, karşılaştırın ve ilginizi belirtin – ekibiniz Viva Porta ile bu kadar düzenli çalışır.', demoCtaTitle: 'İlk eşleşmenize hazır mısınız?', demoCtaLabel: 'Personel ihtiyacını görüşün',
  },
};

export const refreshCompanyJourneyCopy = (content: LandingContent, fallback: LandingContent, locale: LandingLocale) => {
  const hero = content.companyPlatformHero;
  const previous = previousCopy[locale];
  for (const key of ['badge', 'title', 'highlight', 'intro', 'demoTitle', 'demoIntro', 'demoCtaTitle', 'demoCtaLabel'] as const) {
    if (hero[key] === previous[key]) hero[key] = fallback.companyPlatformHero[key];
  }
  for (const action of ['primaryAction', 'secondaryAction'] as const) {
    if (hero[action].label === previous[action].label) hero[action].label = fallback.companyPlatformHero[action].label;
  }
  const previousMatching = locale === 'de' ? {
    emptyText: 'Passen Sie Suche oder Tätigkeitsfilter an.',
    ctaText: 'Wir zeigen Ihnen passende Profile und den persönlichen Ablauf für Ihren Bedarf.',
    withdrawDescription: 'Sie können das Profil in dieser Demo jederzeit erneut vormerken.',
  } : locale === 'tr' ? {
    emptyText: 'Aramanızı veya çalışma alanı filtrenizi değiştirin.',
    ctaText: 'İhtiyaçlarınıza uygun profilleri ve sonraki adımları birlikte inceleyelim.',
    withdrawDescription: 'Bu demoda profili istediğiniz zaman tekrar işaretleyebilirsiniz.',
  } : undefined;
  if (previousMatching) {
    for (const field of ['emptyText', 'ctaText'] as const) {
      if (hero.matching[field] === previousMatching[field]) hero.matching[field] = fallback.companyPlatformHero.matching[field];
    }
    if (hero.matching.labels.withdrawDescription === previousMatching.withdrawDescription) hero.matching.labels.withdrawDescription = fallback.companyPlatformHero.matching.labels.withdrawDescription;
  }
};

/** Revision-21 migration: the company hero's discovery CTA now starts the public company application flow. */
export const migrateCompanyHeroActionLink = (content: LandingContent, fallback: LandingContent): void => {
  if (content.companyPlatformHero.primaryAction.href === '#matching-demo') {
    content.companyPlatformHero.primaryAction.href = fallback.companyPlatformHero.primaryAction.href;
  }
};
